import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import DataTable from "@/components/DataTable";
import OperadorForm from "@/components/OperadorForm";
import GtfsDialog from "@/components/GtfsDialog";
import TurnosDialog from "@/components/TurnosDialog";
import { toast } from "sonner";
import {
  Building2, Bus, Route, MapPin, ListOrdered, Warehouse, Users, Truck,
  CalendarDays, Clock, Download, UploadCloud, Trash2, ChevronDown,
} from "lucide-react";

const ICONS = {
  operador: Building2, lineas: Bus, itinerarios: Route, paradas: MapPin,
  itinerario_paradas: ListOrdered, cocheras: Warehouse, empleados: Users,
  vehiculos: Truck, calendarios: CalendarDays, turnos: Clock,
};

export default function Workspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canWrite = user?.role === "admin" || user?.role === "operador";
  const [operadores, setOperadores] = useState([]);
  const [schemas, setSchemas] = useState(null);
  const [order, setOrder] = useState([]);
  const [operador, setOperador] = useState(null);
  const [active, setActive] = useState("operador");
  const [gtfsOpen, setGtfsOpen] = useState(false);
  const [turnosOpen, setTurnosOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const loadOperadores = useCallback(async () => {
    const res = await api.get("/operadores");
    setOperadores(res.data);
  }, []);

  useEffect(() => {
    api.get("/schemas").then((res) => { setSchemas(res.data.schemas); setOrder(res.data.order); });
  }, []);

  useEffect(() => {
    loadOperadores();
  }, [loadOperadores]);

  useEffect(() => {
    setActive("operador");
    api.get(`/operadores/${id}`).then((res) => setOperador(res.data)).catch(() => navigate("/"));
  }, [id, navigate]);

  const download = async (path, fallbackName) => {
    try {
      const res = await fetch(`${API}${path}`, { credentials: "include" });
      if (!res.ok) throw new Error("Error de exportación");
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const name = m ? m[1] : fallbackName;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel generado");
    } catch (e) {
      toast.error(formatApiError(e.message));
    }
    setExportOpen(false);
  };

  const deleteOperador = async () => {
    if (!window.confirm("¿Eliminar este operador y TODOS sus datos? Esta acción no se puede deshacer.")) return;
    await api.delete(`/operadores/${id}`);
    toast.success("Operador eliminado");
    await loadOperadores();
    navigate("/");
  };

  if (!schemas || !operador) {
    return (
      <AppShell operadores={operadores} reloadOperadores={loadOperadores}>
        <div className="flex-1 flex items-center justify-center text-muted-foreground">Cargando…</div>
      </AppShell>
    );
  }

  return (
    <AppShell operadores={operadores} reloadOperadores={loadOperadores}>
      <header className="h-16 flex items-center justify-between px-6 border-b border-border bg-card sticky top-0 z-10">
        <div className="min-w-0">
          <h1 className="text-xl font-bold tracking-tight text-slate-900 truncate" style={{ fontFamily: "Work Sans" }}>
            {operador.nombre}
          </h1>
          <p className="text-xs text-muted-foreground">{operador.nombre_corto}{operador.codigo_operador ? ` · Código ${operador.codigo_operador}` : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setGtfsOpen(true)} data-testid="import-gtfs-btn" hidden={!canWrite} className={`inline-flex items-center gap-2 rounded-md border border-border bg-card h-9 px-3 text-sm font-medium hover:bg-slate-50 shadow-sm ${canWrite ? "" : "hidden"}`}>
            <UploadCloud size={16} /> Importar GTFS
          </button>
          <button onClick={() => setTurnosOpen(true)} data-testid="import-turnos-btn" hidden={!canWrite} className={`inline-flex items-center gap-2 rounded-md border border-border bg-card h-9 px-3 text-sm font-medium hover:bg-slate-50 shadow-sm ${canWrite ? "" : "hidden"}`}>
            <UploadCloud size={16} /> Importar Turnos
          </button>
          <div className="relative">
            <button onClick={() => setExportOpen((v) => !v)} data-testid="export-menu-btn" className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground h-9 px-3 text-sm font-medium hover:bg-primary/90 shadow-sm">
              <Download size={16} /> Exportar <ChevronDown size={14} />
            </button>
            {exportOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setExportOpen(false)} />
                <div className="absolute right-0 mt-1 w-60 rounded-md border bg-card shadow-lg z-20 py-1" data-testid="export-menu">
                  <button onClick={() => download(`/operadores/${id}/export/ddr`, "DDR.xlsx")} data-testid="export-ddr" className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">DDR-{(operador.nombre_corto||"OP").toUpperCase()}.xlsx</button>
                  <button onClick={() => download(`/operadores/${id}/export/empleados-vehiculos`, "Empleados-Vehiculos.xlsx")} data-testid="export-emp" className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">Empleados-Vehículos.xlsx</button>
                  <button onClick={() => download(`/operadores/${id}/export/turnos`, "Turnos.xlsx")} data-testid="export-turnos" className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">Turnos.xlsx</button>
                </div>
              </>
            )}
          </div>
          <button onClick={deleteOperador} data-testid="delete-operador-btn" hidden={!isAdmin} className={`inline-flex items-center justify-center rounded-md border border-border bg-card h-9 w-9 hover:bg-red-50 hover:text-destructive text-slate-500 shadow-sm ${isAdmin ? "" : "hidden"}`}>
            <Trash2 size={16} />
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="px-6 pt-4 border-b border-border bg-card">
        <div className="flex gap-1 overflow-x-auto attg-scroll pb-0">
          {order.map((ent) => {
            const Icon = ICONS[ent] || Building2;
            return (
              <button
                key={ent}
                onClick={() => setActive(ent)}
                data-testid={`tab-${ent}`}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  active === ent ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
              >
                <Icon size={15} /> {schemas[ent].label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-auto attg-scroll p-6" data-testid="workspace-content">
        {active === "operador" ? (
          <OperadorForm opId={id} schema={schemas.operador} />
        ) : (
          <DataTable key={`${active}-${reloadKey}`} opId={id} entity={active} schema={schemas[active]} />
        )}
      </div>

      <GtfsDialog open={gtfsOpen} opId={id} onClose={() => setGtfsOpen(false)} onDone={() => { setReloadKey((k) => k + 1); loadOperadores(); }} />
      <TurnosDialog open={turnosOpen} opId={id} onClose={() => setTurnosOpen(false)} onDone={() => { setReloadKey((k) => k + 1); loadOperadores(); }} />
    </AppShell>
  );
}
